import { useEffect, useMemo, useRef, useState } from 'react';
import { OverlayDialog } from '../../components/ux/OverlayDialog';
import { SectionTabs } from '../../components/ux/SectionTabs';
import { InkReplayViewer } from '../../features/ink/InkReplayViewer';
import { assignmentTypeLabel, buildLearningSessionSummaries, type HistoryAnswerRow, type HistoryAssignmentItemRow, type HistoryAssignmentRow, type HistoryAttemptRow, type HistorySessionRow, type LearningSessionSummary } from '../../features/history/sessionHistory';
import { formatDuration } from '../../features/pilotMath/format';
import { ensureCurrentMathLevelProgress, type LevelProgressSummary } from '../../features/progression/levelProgress';
import { downloadSupabaseInkDocument, getSupabaseRuntimeIdentity } from '../../features/runtime/supabaseAdapter';
import { getSupabaseClient } from '../../lib/supabase';
import type { InkDocumentV1 } from '../../types/ink';

type ProgressRow = { skill_id: string; mastery_state: string; mastery_percent: number; evidence_count: number; first_pass_accuracy: number | null; independent_accuracy: number | null; average_active_seconds: number | null; last_activity_at: string | null };
type SkillRow = { id: string; code: string; learner_statement: string };
type EvidenceRow = { id: string; skill_id: string; source_id: string | null; source_type: string; evidence_type: string; first_attempt_correct: boolean | null; final_correct: boolean | null; hint_count: number; active_duration_ms: number; evidence_weight: number; occurred_at: string };
type CorrectionRow = { attempt_answer_id: string; previous_response: unknown; corrected_response: unknown; correct_after_correction: boolean };
type InkRow = { attempt_answer_id: string; storage_key: string; stroke_count: number; point_count: number; input_type: string; duration_ms: number; capture_replay: boolean };

function responseValue(value: unknown) {
  if (!value || typeof value !== 'object') return '—';
  const row = value as Record<string, unknown>;
  return row.value === undefined ? '—' : String(row.value);
}

function stateLabel(value: string) {
  if (value === 'mastered') return 'Sudah kuat';
  if (value === 'provisional_mastery') return 'Hampir kuat';
  if (value === 'practicing') return 'Sedang dilatih';
  if (value === 'learning') return 'Sedang dipelajari';
  if (value === 'needs_review' || value === 'review_due') return 'Perlu diulang';
  if (value === 'remedial') return 'Perlu penguatan';
  if (value === 'available') return 'Siap dipelajari';
  return value.replaceAll('_', ' ');
}

type ProgressSection = 'overview' | 'skills' | 'history';

const progressTabs = [
  { value: 'overview', label: 'Ringkasan', description: 'Level & tahap' },
  { value: 'skills', label: 'Kemampuan', description: 'Yang dipelajari' },
  { value: 'history', label: 'Riwayat', description: 'Sesi & tulisan' },
];

export function CloudStudentProgressPage() {
  const [progress, setProgress] = useState<ProgressRow[]>([]);
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [corrections, setCorrections] = useState<CorrectionRow[]>([]);
  const [inks, setInks] = useState<InkRow[]>([]);
  const [level, setLevel] = useState<LevelProgressSummary | null>(null);
  const [studentName, setStudentName] = useState('');
  const [error, setError] = useState('');
  const [replayTitle, setReplayTitle] = useState('');
  const [replayDocument, setReplayDocument] = useState<InkDocumentV1 | null>(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState('');
  const [sessions, setSessions] = useState<LearningSessionSummary[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [activeSection, setActiveSection] = useState<ProgressSection>('overview');
  const replayRequestId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const client = getSupabaseClient();
        if (!client) throw new Error('BelajarKu belum terhubung ke layanan belajar.');
        const identity = await getSupabaseRuntimeIdentity();
        if (!identity?.student) throw new Error('Profil murid belum tersedia.');
        setStudentName(identity.student.displayName);
        const [p, currentLevel] = await Promise.all([
          client.from('student_skill_progress').select('skill_id,mastery_state,mastery_percent,evidence_count,first_pass_accuracy,independent_accuracy,average_active_seconds,last_activity_at').eq('student_id', identity.student.id).order('mastery_percent', { ascending: false }),
          ensureCurrentMathLevelProgress(),
        ]);
        if (p.error) throw p.error;
        const progressRows = (p.data ?? []) as ProgressRow[];
        const ids = [...new Set(progressRows.map((row) => row.skill_id))];
        const s = ids.length ? await client.from('skills').select('id,code,learner_statement').in('id', ids) : { data: [], error: null };
        if (s.error) throw s.error;
        const ev = await client.from('skill_evidence_events').select('id,skill_id,source_id,source_type,evidence_type,first_attempt_correct,final_correct,hint_count,active_duration_ms,evidence_weight,occurred_at').eq('student_id', identity.student.id).order('occurred_at', { ascending: false }).limit(60);
        if (ev.error) throw ev.error;
        const evidenceRows = (ev.data ?? []) as EvidenceRow[];

        const assignmentResult = await client.from('assignments').select('id,assigned_date,assignment_type,status,updated_at').eq('student_id', identity.student.id).order('assigned_date', { ascending: false }).limit(20);
        if (assignmentResult.error) throw assignmentResult.error;
        const assignmentRows = (assignmentResult.data ?? []) as HistoryAssignmentRow[];
        const assignmentIds = assignmentRows.map((row) => row.id);
        const itemResult = assignmentIds.length ? await client.from('assignment_items').select('id,assignment_id,sequence').in('assignment_id', assignmentIds) : { data: [], error: null };
        if (itemResult.error) throw itemResult.error;
        const assignmentItemRows = (itemResult.data ?? []) as HistoryAssignmentItemRow[];
        const itemIds = assignmentItemRows.map((row) => row.id);
        const attemptResult = itemIds.length ? await client.from('attempts').select('id,assignment_item_id,learning_session_id,status,started_at,submitted_at,active_duration_ms').in('assignment_item_id', itemIds).order('started_at', { ascending: false }) : { data: [], error: null };
        if (attemptResult.error) throw attemptResult.error;
        const attemptRows = (attemptResult.data ?? []) as HistoryAttemptRow[];
        const attemptIds = attemptRows.map((row) => row.id);
        const answerResult = attemptIds.length ? await client.from('attempt_answers').select('id,attempt_id,first_attempt_correct,final_correct,hint_count,active_duration_ms,answered_at').in('attempt_id', attemptIds) : { data: [], error: null };
        if (answerResult.error) throw answerResult.error;
        const answerRows = (answerResult.data ?? []) as HistoryAnswerRow[];
        const sessionResult = assignmentIds.length ? await client.from('learning_sessions').select('id,assignment_id,started_at,ended_at,active_duration_ms,status').eq('student_id', identity.student.id).in('assignment_id', assignmentIds).order('started_at', { ascending: false }).limit(20) : { data: [], error: null };
        if (sessionResult.error) throw sessionResult.error;
        const sessionRows = (sessionResult.data ?? []) as HistorySessionRow[];
        const sessionSummaries = buildLearningSessionSummaries({ assignments: assignmentRows, assignmentItems: assignmentItemRows, sessions: sessionRows, attempts: attemptRows, answers: answerRows });

        const historyAnswerIds = answerRows.map((row) => row.id);
        const evidenceAnswerIds = evidenceRows.map((row) => row.source_id).filter((value): value is string => Boolean(value));
        const answerIds = [...new Set([...historyAnswerIds, ...evidenceAnswerIds])];
        const c = answerIds.length ? await client.from('corrections').select('attempt_answer_id,previous_response,corrected_response,correct_after_correction').in('attempt_answer_id', answerIds) : { data: [], error: null };
        if (c.error) throw c.error;
        const ink = answerIds.length ? await client.from('answer_ink_records').select('attempt_answer_id,storage_key,stroke_count,point_count,input_type,duration_ms,capture_replay').in('attempt_answer_id', answerIds) : { data: [], error: null };
        if (ink.error) throw ink.error;
        if (cancelled) return;
        setProgress(progressRows);
        setSkills((s.data ?? []) as SkillRow[]);
        setEvidence(evidenceRows);
        setCorrections((c.data ?? []) as CorrectionRow[]);
        setInks((ink.data ?? []) as InkRow[]);
        setSessions(sessionSummaries);
        setSelectedSessionId((current) => current || sessionSummaries[0]?.sessionId || '');
        setLevel(currentLevel);
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Progress belum dapat dimuat.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const skillMap = useMemo(() => new Map(skills.map((row) => [row.id, row])), [skills]);
  const correctionMap = useMemo(() => new Map(corrections.map((row) => [row.attempt_answer_id, row])), [corrections]);
  const inkMap = useMemo(() => new Map(inks.map((row) => [row.attempt_answer_id, row])), [inks]);
  const totalActiveMs = useMemo(() => evidence.reduce((sum, row) => sum + Number(row.active_duration_ms || 0), 0), [evidence]);
  const levelPercent = Math.round(level?.progressPercent ?? 0);
  const levelName = level?.levelName || 'Level C';
  const selectedSession = useMemo(() => sessions.find((row) => row.sessionId === selectedSessionId) ?? sessions[0] ?? null, [sessions, selectedSessionId]);
  const selectedSessionAnswerIds = useMemo(() => new Set(selectedSession?.answerIds ?? []), [selectedSession]);
  const selectedSessionAnswerOrder = useMemo(() => new Map((selectedSession?.answerIds ?? []).map((id, index) => [id, index + 1])), [selectedSession]);
  const selectedSessionEvidence = useMemo(() => evidence
    .filter((row) => row.source_id && selectedSessionAnswerIds.has(row.source_id))
    .sort((a, b) => (selectedSessionAnswerOrder.get(a.source_id ?? '') ?? Number.MAX_SAFE_INTEGER) - (selectedSessionAnswerOrder.get(b.source_id ?? '') ?? Number.MAX_SAFE_INTEGER)), [evidence, selectedSessionAnswerIds, selectedSessionAnswerOrder]);

  async function openReplay(ink: InkRow, title: string) {
    const requestId = ++replayRequestId.current;
    setReplayLoading(true);
    setReplayError('');
    setReplayDocument(null);
    setReplayTitle(title);
    try {
      const document = await downloadSupabaseInkDocument(ink.storage_key);
      if (requestId === replayRequestId.current) setReplayDocument(document);
    } catch (cause) {
      if (requestId === replayRequestId.current) setReplayError(cause instanceof Error ? cause.message : 'Rekaman tulisan belum dapat dibuka.');
    } finally {
      if (requestId === replayRequestId.current) setReplayLoading(false);
    }
  }

  function closeReplay() {
    replayRequestId.current += 1;
    setReplayLoading(false);
    setReplayDocument(null);
    setReplayTitle('');
    setReplayError('');
  }

  if (error) return <div className="feedback feedback--error">{error}</div>;

  return (
    <div className="stack production-page student-progress-page bkux-progress-page">
      <header className="production-page__heading">
        <div>
          <span className="page-kicker">PROGRESS</span>
          <h1>Perjalanan belajar {studentName || 'kamu'}</h1>
          <p className="muted">Lihat level, kemampuan, dan riwayat latihan tanpa harus menggulir satu halaman panjang.</p>
        </div>
      </header>

      <SectionTabs
        value={activeSection}
        tabs={progressTabs}
        ariaLabel="Bagian progress belajar"
        onChange={(value) => setActiveSection(value as ProgressSection)}
      />

      {activeSection === 'overview' ? (
        <div className="bkux-progress-section" aria-label="Ringkasan progress">
          <section className="level-journey-card">
            <div className="level-journey-card__copy">
              <span className="page-kicker">MATEMATIKA · LEVEL SEKARANG</span>
              <div className="level-journey-card__title"><h2>{levelName}</h2><strong>{levelPercent}%</strong></div>
              <div className="level-journey-track"><span style={{ width: `${levelPercent}%` }} /></div>
              <p>Progress bertambah saat satu tahap kurikulum benar-benar selesai. Kemampuan yang sudah dipelajari tetap tersimpan tanpa membuat level baru meloncat.</p>
            </div>
            <div className="level-journey-card__stats">
              <div><strong>{level?.checkpointTarget ? `${level.checkpointCompleted}/${level.checkpointTarget}` : '—'}</strong><span>tahap selesai</span></div>
              <div><strong>{level?.checkpointInProgress ?? 0}</strong><span>sedang dikerjakan</span></div>
              <div><strong>{formatDuration(totalActiveMs)}</strong><span>waktu riwayat terbaru</span></div>
            </div>
          </section>

          {level?.units.length ? (
            <section className="card progress-panel curriculum-journey-panel">
              <div className="section-heading">
                <div><span className="page-kicker">ISI LEVEL C</span><h2>Bagian yang sedang ditempuh</h2></div>
                <span className="muted section-note">{level.checkpointCompleted}/{level.checkpointTarget} tahap selesai</span>
              </div>
              {level.nextCheckpoint ? <p className="curriculum-next-step"><b>Yang sedang dituju:</b> {level.nextCheckpoint.title} <span>· {level.nextCheckpoint.unitName}</span></p> : null}
              <div className="curriculum-unit-list">
                {level.units.map((unit, index) => (
                  <article className="curriculum-unit-row" key={unit.unitCode}>
                    <div className="curriculum-unit-row__number">{index + 1}</div>
                    <div className="curriculum-unit-row__body">
                      <div className="curriculum-unit-row__heading"><strong>{unit.unitName}</strong><span>{unit.completedCheckpoints}/{unit.totalCheckpoints} tahap</span></div>
                      <div className="mastery-track"><span style={{ width: `${unit.progressPercent}%` }} /></div>
                    </div>
                  </article>
                ))}
              </div>
              <p className="muted progress-explainer">Satu latihan dapat menambah bukti pada beberapa tahap. Persentase level bertambah setelah kontrak satu tahap terpenuhi.</p>
            </section>
          ) : null}
        </div>
      ) : null}

      {activeSection === 'skills' ? (
        <div className="bkux-progress-section" aria-label="Kemampuan yang sedang dipelajari">
          <section className="card progress-panel">
            <div className="section-heading"><div><span className="page-kicker">KEMAMPUANMU</span><h2>Yang sedang dipelajari</h2></div><span className="availability-pill availability-pill--active">Matematika</span></div>
            <p className="muted progress-explainer">Angka kemampuan menunjukkan seberapa kuat pemahaman berdasarkan latihan yang pernah dikerjakan.</p>
            <div className="mastery-list">
              {progress.map((row) => {
                const skill = skillMap.get(row.skill_id);
                return (
                  <article className="mastery-row" key={row.skill_id}>
                    <div className="mastery-row__copy"><strong>{skill?.learner_statement ?? skill?.code ?? row.skill_id}</strong><span>{stateLabel(row.mastery_state)} · {row.evidence_count} latihan tercatat</span></div>
                    <div className="mastery-row__score"><b>{row.mastery_percent}%</b><div className="mastery-track"><span style={{ width: `${Math.max(0, Math.min(100, row.mastery_percent))}%` }} /></div></div>
                  </article>
                );
              })}
              {!progress.length ? <div className="empty-state">Belum ada kemampuan yang tercatat. Mulai latihan pertama untuk melihat perkembangannya.</div> : null}
            </div>
          </section>
        </div>
      ) : null}

      {activeSection === 'history' ? (
        <div className="bkux-progress-section" aria-label="Riwayat belajar">
          <section className="card progress-panel session-history-panel bkux-history-panel">
            <div className="section-heading">
              <div><span className="page-kicker">RIWAYAT SESI</span><h2>Latihan yang sudah kamu jalani</h2></div>
              <span className="muted section-note">Pilih sesi, lalu lihat proses dan tulisan</span>
            </div>
            <div className="session-history-layout">
              <div className="session-history-list" aria-label="Daftar sesi latihan">
                {sessions.map((session) => {
                  const selected = selectedSession?.sessionId === session.sessionId;
                  const firstPassPercent = session.answerCount ? Math.round((session.firstPassCorrect / session.answerCount) * 100) : 0;
                  return (
                    <button
                      className={`session-history-row ${selected ? 'session-history-row--selected' : ''}`}
                      key={session.sessionId}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setSelectedSessionId(session.sessionId)}
                    >
                      <span className="session-history-row__date">{new Date(`${session.learningDate}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className="session-history-row__title">{assignmentTypeLabel(session.assignmentType)} Matematika</span>
                      <span className="session-history-row__meta">{session.answerCount} soal · {firstPassPercent}% benar pertama · {formatDuration(session.activeDurationMs)}</span>
                    </button>
                  );
                })}
                {!sessions.length ? <div className="empty-state">Belum ada sesi latihan yang tersimpan.</div> : null}
              </div>

              <div className="session-history-detail">
                {selectedSession ? (
                  <>
                    <div className="session-history-detail__hero">
                      <div><span className="page-kicker">DETAIL SESI</span><h3>{assignmentTypeLabel(selectedSession.assignmentType)} · {new Date(`${selectedSession.learningDate}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</h3></div>
                      <span className="availability-pill availability-pill--active">{selectedSession.status === 'completed' ? 'Selesai' : 'Tersimpan'}</span>
                    </div>
                    <div className="session-history-stats">
                      <div><strong>{selectedSession.answerCount}</strong><span>soal</span></div>
                      <div><strong>{selectedSession.firstPassCorrect}</strong><span>benar pertama</span></div>
                      <div><strong>{selectedSession.correctionNeeded}</strong><span>diperbaiki</span></div>
                      <div><strong>{formatDuration(selectedSession.activeDurationMs)}</strong><span>waktu aktif</span></div>
                    </div>
                    <div className="session-history-evidence">
                      {selectedSessionEvidence.map((row) => {
                        const skill = skillMap.get(row.skill_id);
                        const correction = row.source_id ? correctionMap.get(row.source_id) : undefined;
                        const ink = row.source_id ? inkMap.get(row.source_id) : undefined;
                        const title = skill?.learner_statement ?? skill?.code ?? 'Latihan Matematika';
                        const questionNumber = row.source_id ? selectedSessionAnswerOrder.get(row.source_id) : undefined;
                        const result = row.first_attempt_correct ? 'Benar pada jawaban pertama' : row.final_correct ? 'Benar setelah diperbaiki' : 'Perlu dilihat lagi';
                        return (
                          <article className="session-history-answer bkux-history-answer" key={row.id}>
                            <div className="bkux-history-answer__copy">
                              {questionNumber ? <span className="bkux-question-label">Soal {questionNumber}</span> : null}
                              <strong>{title}</strong>
                              <span className="bkux-answer-state">{result}</span>
                            </div>
                            <div className="session-history-answer__actions bkux-history-answer__actions">
                              {correction ? <span className="bkux-correction-pill">Perbaikan {responseValue(correction.previous_response)} → {responseValue(correction.corrected_response)}</span> : null}
                              {ink?.capture_replay ? (
                                <button className="bkux-replay-button" type="button" onClick={() => void openReplay(ink, title)} aria-label={`Putar tulisan soal ${questionNumber ?? ''}`.trim()}>
                                  <span aria-hidden="true">✍</span> Putar tulisan
                                </button>
                              ) : null}
                            </div>
                          </article>
                        );
                      })}
                      {!selectedSessionEvidence.length ? <p className="muted">Detail evidence sesi ini belum tersedia. Ringkasan sesi tetap ditampilkan tanpa mengarang detail.</p> : null}
                    </div>
                  </>
                ) : <div className="empty-state">Pilih satu sesi untuk melihat detail.</div>}
              </div>
            </div>
          </section>

          <details className="card progress-panel bkux-archive-details">
            <summary><span>Riwayat aktivitas lengkap</span><span className="muted">Jawaban · perbaikan · tulisan</span></summary>
            <div className="bkux-archive-details__body">
              <div className="record-timeline">
                {evidence.map((row) => {
                  const skill = skillMap.get(row.skill_id);
                  const correction = row.source_id ? correctionMap.get(row.source_id) : undefined;
                  const ink = row.source_id ? inkMap.get(row.source_id) : undefined;
                  const result = row.first_attempt_correct ? 'Benar pada jawaban pertama' : row.final_correct ? 'Benar setelah diperbaiki' : 'Perlu dilihat lagi';
                  const title = skill?.learner_statement ?? skill?.code ?? 'Latihan Matematika';
                  return (
                    <article className="record-event" key={row.id}>
                      <div className={`record-event__icon ${row.final_correct ? 'record-event__icon--ok' : ''}`}>{ink ? '✍' : row.final_correct ? '✓' : '!'}</div>
                      <div className="record-event__body">
                        <div className="record-event__heading"><strong>{title}</strong><span>{new Date(row.occurred_at).toLocaleString('id-ID')}</span></div>
                        <div className="record-event__chips"><span>{result}</span><span>{formatDuration(row.active_duration_ms)}</span>{row.hint_count > 0 ? <span>{row.hint_count} bantuan</span> : null}</div>
                        {correction ? <p className="record-event__correction">Jawaban diperbaiki: <b>{responseValue(correction.previous_response)}</b> → <b>{responseValue(correction.corrected_response)}</b></p> : null}
                        {ink ? (
                          <div className="record-event__ink-row">
                            <span>✍ {ink.stroke_count} goresan tersimpan</span>
                            {ink.capture_replay ? <button className="text-button" type="button" onClick={() => void openReplay(ink, title)}>Putar ulang tulisan</button> : null}
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
                {!evidence.length ? <div className="empty-state">Belum ada riwayat. Selesaikan latihan pertamamu dan riwayat akan muncul di sini.</div> : null}
              </div>
            </div>
          </details>
        </div>
      ) : null}

      {replayTitle || replayLoading || replayDocument || replayError ? (
        <OverlayDialog title={replayTitle || 'Tulisanmu'} kicker="REKAMAN TULISAN" onClose={closeReplay}>
          {replayLoading ? <div className="empty-state" aria-live="polite">Mengambil rekaman tulisan…</div> : null}
          {replayError ? <div className="feedback feedback--error" role="alert">{replayError}</div> : null}
          {replayDocument ? <InkReplayViewer document={replayDocument} /> : null}
        </OverlayDialog>
      ) : null}
    </div>
  );
}
